import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabaseClient } from '../../config/supabase';

export const ADMIN_LINKS = [
    { label: 'Overview', path: '/admin' },
    { label: 'Brands & Locations', path: '/admin/organization' },
    { label: 'Company Words', path: '/admin/company-words' },
    { label: 'Billing & Financials', path: '/admin/financials' },
    { label: 'Staff & Roles', path: '/admin/personnel' },
    { label: 'Applicant Pipeline', path: '/admin/applicants' },
    { label: 'Notification Defaults', path: '/admin/notifications' },
    { label: 'Phone Numbers', path: '/admin/email-channel?tab=numbers' },
    { label: 'Email Mailboxes', path: '/admin/email-channel' },
    { label: 'Calendar Connections', path: '/admin/calendar-connections' },
    { label: 'Business Schedule', path: '/admin/business-schedule' },
    { label: 'Client Invitations', path: '/admin/client-invitations' },
    { label: 'Templates', path: '/admin/email-channel?tab=templates' },
    { label: 'Signatures', path: '/admin/signatures' },
    { label: 'Customer Consent', path: '/admin/email-channel?tab=customer-consent' },
    { label: 'Possible Duplicates', path: '/admin/crm/duplicates' },
    { label: 'Reports & Logs', path: '/admin/reports-logs' },
    { label: 'Support Access', path: '/admin/support-access' },
    // Breaker Panel G3: Live/Test per module (Owners and Tenant Admins).
    { label: 'Go-Live Panel', path: '/admin/go-live' },
];

// EV-fix-1 (Module 19): the Events entry joins the Admin Center menu only while the Events pack is on for this organization
// and the flag has finished loading. It is never listed while the flag is still loading, so it cannot show and then disappear.
export const EVENTS_ADMIN_LINK = { label: 'Events', path: '/admin/events' };

export function showEventsAdmin({ eventsEnabled = false, eventsLoading = false } = {}) {
    return Boolean(eventsEnabled) && !eventsLoading;
}

// Teams PR A: Integrations > Microsoft Teams joins the menu only for a Tenant Admin whose organization opted in to Teams
// (CRMApp passes the database's answer, already resolved; never shown while it loads).
export const TEAMS_ADMIN_LINK = { label: 'Microsoft Teams', path: '/admin/integrations/microsoft-teams' };

export function adminMenuLinks(options = {}) {
    const links = showEventsAdmin(options) ? [...ADMIN_LINKS, EVENTS_ADMIN_LINK] : ADMIN_LINKS;
    return options.teamsEnabled ? [...links, TEAMS_ADMIN_LINK] : links;
}

export default function AdminPanelShell({ children, theme = 'light', eventsEnabled = false, eventsLoading = false, teamsEnabled = false }) {
    const { pathname, search } = useLocation();
    const menuLinks = adminMenuLinks({ eventsEnabled, eventsLoading, teamsEnabled });
    const [access, setAccess] = useState({ status: 'checking', error: '' });

    useEffect(() => {
        let active = true;

        async function verifyAccess() {
            const { data: orgAdmin, error: orgAdminError } = await supabaseClient.rpc('is_org_admin');
            if (!active) return;
            if (orgAdminError) {
                setAccess({ status: 'error', error: orgAdminError.message });
                return;
            }
            if (orgAdmin) {
                setAccess({ status: 'allowed', error: '' });
                return;
            }

            const { data: platformAdmin, error: platformAdminError } = await supabaseClient.rpc('is_platform_super_admin');
            if (!active) return;
            if (platformAdminError) {
                setAccess({ status: 'error', error: platformAdminError.message });
                return;
            }
            setAccess({ status: platformAdmin ? 'allowed' : 'denied', error: '' });
        }

        verifyAccess().catch((error) => {
            if (active) setAccess({ status: 'error', error: error.message || 'Admin access could not be verified.' });
        });

        return () => { active = false; };
    }, []);

    if (access.status === 'checking') {
        return <div className="p-8 text-center text-[var(--ink-muted)]">Checking admin access…</div>;
    }
    if (access.status === 'error') {
        return (
            <div role="alert" className="rounded-lg border border-red-500/50 bg-red-500/10 p-4 text-sm text-[var(--ink)]">
                Could not verify admin access: {access.error}
            </div>
        );
    }
    if (access.status === 'denied') {
        return (
            <div role="alert" className="rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-6 text-[var(--ink)]">
                This area is available to organization admins only.
            </div>
        );
    }

    const activeLink = menuLinks.find((link) => {
        const [linkPath, linkSearch = ''] = link.path.split('?');
        return linkPath === pathname && `?${linkSearch}` === search;
    }) || menuLinks.find((link) => link.path === pathname) || menuLinks[0];
    return (
        <div data-theme={theme} className="ia-admin -mx-4 -my-4 flex flex-col lg:flex-row min-h-full bg-[var(--surface-canvas)] text-[var(--ink)]">
            <aside
                className="lg:sticky lg:top-0 lg:max-h-screen w-full lg:w-64 lg:shrink-0 overflow-y-auto border-b lg:border-b-0 lg:border-r border-[var(--surface-border)] bg-[var(--surface-raised)] px-4 py-5"
                aria-label="Admin navigation"
            >
                <Link to="/admin" className="mb-6 block px-2 text-lg font-semibold text-[var(--ink)]">
                    Admin Center
                </Link>
                <nav className="space-y-1">
                    {menuLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            aria-current={activeLink.path === link.path ? 'page' : undefined}
                            className={`block rounded px-3 py-2 text-sm ${
                                activeLink.path === link.path
                                    ? 'border-l-4 border-[var(--selected-pill-border)] bg-green-500/10 font-semibold text-[var(--selected-pill-ink)]'
                                    : 'border-l-4 border-transparent text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)]'
                            }`}
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>
            </aside>
            <main className="min-w-0 flex-1 px-6 py-6">
                <div className="mb-4 text-sm text-[var(--ink-muted)]">
                    <Link to="/admin" className="hover:text-[#03B7C9]">Admin Center</Link>
                    {activeLink.path !== '/admin' && ` › ${activeLink.label}`}
                </div>
                <header className="mb-5">
                    <h1 className="text-3xl font-semibold text-[var(--ink)]">
                        {activeLink.label}
                    </h1>
                    <p className="mt-1 text-sm text-[var(--ink-muted)]">
                        Organization administration
                    </p>
                </header>
                <div className="min-h-[calc(100%-6rem)] rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-5">
                    {children}
                </div>
            </main>
        </div>
    );
}
